import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Cable as CableIcon,
  CheckCircle,
  Close as CloseIcon,
  Search as SearchIcon,
  SkipNext,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { apiService, ConnectorConfig, Project, Schema } from '../services/api';
import { CatalogConnector, CatalogResponse } from '../types/connectorCatalog';
import ConnectorCard from './ConnectorCatalog/ConnectorCard';
import ConnectorSetupDialog from './ConnectorCatalog/ConnectorSetupDialog';

/* ---------- Types ---------- */

export type ConnectorChoice =
  | { kind: 'existing'; connectorId: string; name: string }
  | {
      kind: 'new';
      displayName: string;
      pendingData: {
        connector_type: string;
        name: string;
        direction: 'source' | 'destination';
        config: Record<string, any>;
        catalog_connector_id: string;
      };
    };

export interface CreateProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: {
    name: string;
    description: string;
    status: string;
    targetSchemaId?: string;
    tags?: string;
    sourceChoice?: ConnectorChoice;
    destChoice?: ConnectorChoice;
  }) => void;
  isSubmitting?: boolean;
  error?: string | null;
  defaultSchemaId?: string;
  defaultStatus?: 'active' | 'draft' | 'on_hold' | 'archived';
}

/* ---------- Constants ---------- */

const WIZARD_STEPS = ['Project Details', 'Choose a Source', 'Choose a Destination'];
const PROJECT_NAME_MAX_LENGTH = 255;

/* ---------- Component ---------- */

const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  error = null,
  defaultSchemaId = '',
  defaultStatus = 'active',
}) => {
  const theme = useTheme();

  // ---- Wizard step ----
  const [activeStep, setActiveStep] = useState(0);

  // ---- Step 0: Project details ----
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [schemaId, setSchemaId] = useState(defaultSchemaId);
  const [status, setStatus] = useState(defaultStatus);
  const [tagsInput, setTagsInput] = useState<string[]>([]);

  // ---- Steps 1 & 2: Connector choices ----
  const [sourceChoice, setSourceChoice] = useState<ConnectorChoice | null>(null);
  const [destChoice, setDestChoice] = useState<ConnectorChoice | null>(null);
  const [search, setSearch] = useState('');

  // ---- ConnectorSetupDialog state ----
  const [setupConnector, setSetupConnector] = useState<CatalogConnector | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);

  // ---- Data queries ----
  const { data: projectsForNameCheck = [] } = useQuery<Project[]>(
    ['projects', 'name-uniqueness', 'create-project-dialog'],
    () => apiService.listProjects({ limit: 500, includeDeleted: true }),
    {
      enabled: open,
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const { data: tagSuggestionProjects = [] } = useQuery<Project[]>(
    ['projects', 'tag-suggestions', 'create-project-dialog'],
    () => apiService.listProjects({ limit: 500 }),
    {
      enabled: open,
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const existingTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const p of tagSuggestionProjects) {
      const meta = (p.metadata ?? {}) as Record<string, unknown>;
      const raw = meta.tags;
      if (Array.isArray(raw)) {
        for (const t of raw) {
          if (typeof t === 'string' && t.trim()) tagSet.add(t.trim());
        }
      }
    }
    return Array.from(tagSet).sort();
  }, [tagSuggestionProjects]);

  const { data: schemaSets = [], isLoading: isLoadingSchemaSets } = useQuery<Schema[]>(
    ['schemas', 'create-project-dialog'],
    () => apiService.listSchemas({ status: 'active' }),
    { enabled: open, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );

  const { data: allConnectors = [], isLoading: isLoadingConnectors } = useQuery<ConnectorConfig[]>(
    ['connectors', 'create-project-dialog'],
    () => apiService.listConnectors(),
    { enabled: open, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );

  const { data: catalogData, isLoading: isLoadingCatalog } = useQuery<CatalogResponse>(
    ['connector-catalog', 'create-wizard'],
    async () => {
      return apiService.getConnectorCatalog();
    },
    { enabled: open && activeStep > 0, staleTime: 10 * 60 * 1000 }
  );

  // ---- Derived data ----
  const directionFilter = activeStep === 1 ? 'source' : 'destination';

  const existingConnectors = useMemo(() => {
    if (activeStep < 1) return [];
    return allConnectors.filter(
      (c) => c.direction === directionFilter || c.direction === 'bidirectional'
    );
  }, [allConnectors, directionFilter, activeStep]);

  const filteredCatalog = useMemo(() => {
    if (!catalogData?.connectors) return [];
    let items = catalogData.connectors.filter(
      (c) => c.direction === directionFilter || c.direction === 'both'
    );
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (c) =>
          c.display_name.toLowerCase().includes(q) ||
          c.category.toLowerCase().includes(q) ||
          c.tags.some((t: string) => t.toLowerCase().includes(q))
      );
    }
    return items.sort((a, b) => {
      if (a.is_popular && !b.is_popular) return -1;
      if (!a.is_popular && b.is_popular) return 1;
      return a.display_name.localeCompare(b.display_name);
    });
  }, [catalogData, directionFilter, search]);

  const currentChoice = activeStep === 1 ? sourceChoice : destChoice;
  const setCurrentChoice = activeStep === 1 ? setSourceChoice : setDestChoice;

  // ---- Reset on dialog open ----
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setProjectName('');
      setDescription('');
      setSchemaId(defaultSchemaId);
      setStatus(defaultStatus);
      setTagsInput([]);
      setSourceChoice(null);
      setDestChoice(null);
      setSearch('');
      setSetupConnector(null);
      setSetupOpen(false);
    }
  }, [open, defaultSchemaId, defaultStatus]);

  // ---- Handlers ----
  const handleClose = () => {
    onClose();
  };

  const handleNext = () => {
    setSearch('');
    setActiveStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
  };

  const handleBack = () => {
    setSearch('');
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleSkip = () => {
    setCurrentChoice(null);
    handleNext();
  };

  const handleSubmit = () => {
    const nameLower = projectName.trim().toLowerCase();
    if (projectsForNameCheck.some((p) => p.name.trim().toLowerCase() === nameLower)) {
      return;
    }
    onSubmit({
      name: projectName,
      description,
      status,
      targetSchemaId: schemaId || undefined,
      tags: tagsInput.length > 0 ? tagsInput.join(', ') : undefined,
      sourceChoice: sourceChoice ?? undefined,
      destChoice: destChoice ?? undefined,
    });
  };

  const handleExistingConnectorClick = useCallback(
    (connector: ConnectorConfig) => {
      setCurrentChoice({ kind: 'existing', connectorId: connector.connector_id, name: connector.name });
    },
    [setCurrentChoice]
  );

  const handleCatalogCardClick = useCallback((connector: CatalogConnector) => {
    setSetupConnector(connector);
    setSetupOpen(true);
  }, []);

  const handleSetupSave = useCallback(
    (data: {
      connector_type: string;
      name: string;
      direction: 'source' | 'destination';
      config: Record<string, any>;
      catalog_connector_id: string;
    }) => {
      setCurrentChoice({
        kind: 'new',
        displayName: data.name,
        pendingData: data,
      });
      setSetupOpen(false);
      setSetupConnector(null);
    },
    [setCurrentChoice]
  );

  const isProjectNameOverLimit = projectName.length > PROJECT_NAME_MAX_LENGTH;
  const matchingNameProject = useMemo(() => {
    const nameLower = projectName.trim().toLowerCase();
    if (!nameLower) {
      return undefined;
    }
    return projectsForNameCheck.find((p) => p.name.trim().toLowerCase() === nameLower);
  }, [projectName, projectsForNameCheck]);
  const isDuplicateName = Boolean(matchingNameProject);
  const isDuplicateSoftDeletedName = Boolean(matchingNameProject?.is_deleted);
  const isStep0Valid =
    projectName.trim() !== '' && !isProjectNameOverLimit && !isDuplicateName;

  // ---- Render helpers ----

  const renderStep0 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Project Name"
        fullWidth
        required
        autoFocus
        value={projectName}
        onChange={(e) => setProjectName(e.target.value)}
        size="small"
        disabled={isSubmitting}
        inputProps={{ maxLength: PROJECT_NAME_MAX_LENGTH }}
        error={isProjectNameOverLimit || isDuplicateName}
        helperText={
          isDuplicateSoftDeletedName
            ? 'This name belongs to a deleted project. Hard delete it to reuse the name.'
            : isDuplicateName
              ? 'Project name already exists. Please use a unique name.'
              : isProjectNameOverLimit
              ? `Project name must be at most ${PROJECT_NAME_MAX_LENGTH} characters.`
              : projectName.length > 0
                ? `${projectName.length}/${PROJECT_NAME_MAX_LENGTH}`
                : undefined
        }
      />

      <TextField
        label="Description"
        fullWidth
        multiline
        rows={3}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        size="small"
        disabled={isSubmitting}
      />

      <TextField
        label="Status"
        select
        fullWidth
        value={status}
        onChange={(e) => setStatus(e.target.value as 'active' | 'draft' | 'on_hold' | 'archived')}
        size="small"
        disabled={isSubmitting}
      >
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="draft">Draft</MenuItem>
        <MenuItem value="on_hold">On Hold</MenuItem>
        <MenuItem value="archived">Archived</MenuItem>
      </TextField>

      <TextField
        label="Target Schema Set"
        select
        fullWidth
        value={schemaId}
        onChange={(e) => setSchemaId(e.target.value)}
        helperText={
          schemaSets.length > 0
            ? 'Optional. Select the target schema set used for mapping guardrails.'
            : 'No active schemas found. Create one in Formats before mapping.'
        }
        size="small"
        disabled={isSubmitting || isLoadingSchemaSets}
      >
        <MenuItem value="">
          <em>{schemaSets.length > 0 ? 'Select schema set' : 'No active schemas'}</em>
        </MenuItem>
        {schemaSets.map((ss) => (
          <MenuItem key={ss.schema_id} value={ss.schema_id}>
            {ss.schema_name} (v{ss.version})
          </MenuItem>
        ))}
      </TextField>

      <Autocomplete
        multiple
        freeSolo
        options={existingTags}
        value={tagsInput}
        onChange={(_, newValue) => setTagsInput(newValue as string[])}
        disabled={isSubmitting}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option}
              label={option}
              size="small"
              variant="outlined"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tags"
            size="small"
            helperText="Select existing tags or type to create new ones"
          />
        )}
      />
    </Box>
  );

  const renderConnectorStep = () => {
    const label = activeStep === 1 ? 'source' : 'destination';

    // If a choice is already made, show summary
    if (currentChoice) {
      const choiceName =
        currentChoice.kind === 'existing' ? currentChoice.name : currentChoice.displayName;
      return (
        <Box>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderColor: 'success.light',
              bgcolor: alpha(theme.palette.success.main, 0.04),
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CheckCircle color="success" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {label === 'source' ? 'Source' : 'Destination'} Selected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {choiceName}
                {currentChoice.kind === 'existing' && (
                  <Chip label="Existing" size="small" sx={{ ml: 1 }} variant="outlined" />
                )}
                {currentChoice.kind === 'new' && (
                  <Chip label="New" size="small" sx={{ ml: 1 }} color="primary" variant="outlined" />
                )}
              </Typography>
            </Box>
            <Button
              size="small"
              color="inherit"
              startIcon={<CloseIcon fontSize="small" />}
              onClick={() => setCurrentChoice(null)}
            >
              Clear
            </Button>
          </Paper>
        </Box>
      );
    }

    return (
      <Box>
        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {activeStep === 1
            ? 'Pick a source connector to import data from, or skip to upload files manually.'
            : 'Choose a destination for your transformed data, or skip to set it up later.'}
        </Typography>

        {/* Section 1: Existing connections */}
        {existingConnectors.length > 0 && (
          <>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Use an Existing Connection
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              {existingConnectors.map((conn) => (
                <Paper
                  key={conn.connector_id}
                  variant="outlined"
                  onClick={() => handleExistingConnectorClick(conn)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    transition: 'all 0.15s',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <CableIcon fontSize="small" color="action" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {conn.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {conn.connector_type}
                    </Typography>
                  </Box>
                  <Chip label={conn.direction} size="small" variant="outlined" />
                </Paper>
              ))}
            </Stack>
            <Divider sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                OR CREATE A NEW CONNECTION
              </Typography>
            </Divider>
          </>
        )}

        {/* Section 2: Catalog (create new) */}
        <TextField
          size="small"
          fullWidth
          placeholder={`Search ${label} connectors...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        {isLoadingCatalog ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {filteredCatalog.slice(0, 12).map((connector) => (
              <Grid item xs={6} sm={4} md={3} key={connector.connector_id}>
                <ConnectorCard connector={connector} onClick={handleCatalogCardClick} />
              </Grid>
            ))}
          </Grid>
        )}

        {filteredCatalog.length === 0 && !isLoadingCatalog && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No connectors match your search.
          </Typography>
        )}

        {filteredCatalog.length > 12 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Showing 12 of {filteredCatalog.length}. Use search to narrow results.
          </Typography>
        )}
      </Box>
    );
  };

  // ---- Render ----
  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent sx={{ pt: 2, minHeight: 400 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {WIZARD_STEPS.map((label, idx) => (
              <Step
                key={label}
                completed={
                  idx === 0
                    ? activeStep > 0
                    : idx === 1
                    ? !!sourceChoice
                    : !!destChoice
                }
              >
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Step content */}
          {activeStep === 0 && renderStep0()}
          {(activeStep === 1 || activeStep === 2) && renderConnectorStep()}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {/* Left side */}
          <Box sx={{ flex: 1 }}>
            {activeStep > 0 && (
              <Button onClick={handleBack} startIcon={<ArrowBack />} disabled={isSubmitting}>
                Back
              </Button>
            )}
          </Box>

          {/* Right side */}
          <Stack direction="row" spacing={1}>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>

            {activeStep === 0 && (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForward />}
                disabled={!isStep0Valid}
              >
                Next
              </Button>
            )}

            {activeStep === 1 && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleSkip}
                  endIcon={<SkipNext />}
                >
                  Skip
                </Button>
                {sourceChoice && (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForward />}
                  >
                    Next
                  </Button>
                )}
              </>
            )}

            {activeStep === 2 && (
              <>
                {!destChoice && (
                  <Button
                    variant="outlined"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isStep0Valid}
                    startIcon={isSubmitting ? <CircularProgress size={18} /> : <SkipNext />}
                  >
                    {isSubmitting ? 'Creating...' : 'Skip & Create'}
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isStep0Valid}
                  startIcon={isSubmitting ? <CircularProgress size={18} /> : undefined}
                >
                  {isSubmitting ? 'Creating...' : 'Create Project'}
                </Button>
              </>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Connector setup (opens as a nested dialog from Steps 1/2) */}
      <ConnectorSetupDialog
        open={setupOpen}
        connector={setupConnector}
        onClose={() => {
          setSetupOpen(false);
          setSetupConnector(null);
        }}
        onSave={handleSetupSave}
      />
    </>
  );
};

export default CreateProjectDialog;
